import { useEffect, useRef, useState, useTransition } from 'react';
import { Link } from 'react-router-dom';
import BrandMark from '../components/BrandMark.jsx';
import StatusStepper from '../components/StatusStepper.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { useSaveToast } from '../components/SaveToast.jsx';
import { api, demoStore } from '../lib/api.js';
import {
  appendLocalMessage,
  deleteLocalMessage,
  getLocalMessages,
  seedDemoMessages,
} from '../lib/communicationsStore.js';
import { hasTrackingNumber, mailCarrierLabel, mailTrackingUrl } from '../lib/uspsTracking.js';
import MessageComposer, { MessageBubble } from '../components/MessageComposer.jsx';
import { scrollMessageThreadToLatest } from '../lib/messageThreadScroll.js';
import { formatMatterMoney } from '../lib/matterCurrency.js';

export default function AttorneyPortal() {
  const { user, logout } = useAuth();
  const { flashSave, clearSaveToast } = useSaveToast();
  const [queue, setQueue] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState('');
  const [tracking, setTracking] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [pending, startTransition] = useTransition();
  const [deletingMessageId, setDeletingMessageId] = useState('');
  const threadRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get('/server/attorney_api/jurisdictionQueue');
        if (!cancelled) {
          setQueue(data.queue || []);
          if (data.queue?.[0]) {
            setSelectedId(data.queue[0].ROWID);
            setDraft(data.queue[0].Demand_Draft || '');
            setDueDate(String(data.queue[0].Due_Date || '').slice(0, 10));
          }
        }
      } catch {
        if (!cancelled) {
          const demo = demoStore.matters.filter((m) =>
            ['INTAKE_VALIDATED', 'ATTORNEY_ASSIGNED'].includes(m.Status)
          );
          setQueue(demo);
          if (demo[0]) {
            setSelectedId(demo[0].ROWID);
            setDraft(demo[0].Demand_Draft || '');
            setDueDate(String(demo[0].Due_Date || '').slice(0, 10));
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = queue.find((m) => m.ROWID === selectedId) || null;

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get(`/server/attorney_api/matter/${selectedId}/messages`);
        if (!cancelled) setMessages(data.messages || []);
      } catch {
        if (cancelled) return;
        const demo = demoStore.messages[selectedId] || [];
        seedDemoMessages(selectedId, demo);
        setMessages(getLocalMessages(selectedId));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  useEffect(() => {
    scrollMessageThreadToLatest(threadRef.current);
  }, [selectedId, messages]);

  function selectMatter(matter) {
    setSelectedId(matter.ROWID);
    setDraft(matter.Demand_Draft || '');
    setTracking(matter.Tracking_Number || '');
    setDueDate(String(matter.Due_Date || '').slice(0, 10));
    setReply('');
    clearSaveToast();
  }

  function applyDueDateLocally(matterId, nextDueDate) {
    const normalized = String(nextDueDate || '').trim().slice(0, 10) || null;
    setQueue((prev) =>
      prev.map((m) =>
        m.ROWID === matterId ? { ...m, Due_Date: normalized, Updated_At: new Date().toISOString() } : m
      )
    );
    const idx = demoStore.matters.findIndex((m) => m.ROWID === matterId);
    if (idx >= 0) {
      demoStore.matters[idx] = {
        ...demoStore.matters[idx],
        Due_Date: normalized,
        Updated_At: new Date().toISOString(),
      };
    }
    return normalized;
  }

  function saveDueDate() {
    if (!selected) return;
    startTransition(async () => {
      try {
        const data = await api.put(`/server/attorney_api/matter/${selected.ROWID}/draft`, {
          Demand_Draft: draft,
          Due_Date: dueDate || null,
        });
        const saved = String(data?.matter?.Due_Date || dueDate || '').slice(0, 10) || null;
        setDueDate(saved || '');
        setQueue((prev) =>
          prev.map((m) =>
            m.ROWID === selected.ROWID
              ? { ...m, ...(data.matter || {}), Due_Date: saved, Demand_Draft: draft }
              : m
          )
        );
        flashSave(saved ? 'Due date saved for this demand.' : 'Due date cleared.');
      } catch {
        applyDueDateLocally(selected.ROWID, dueDate);
        flashSave(
          dueDate
            ? 'Due date saved locally (API offline). The client can see it on the demand.'
            : 'Due date cleared locally (API offline).'
        );
      }
    });
  }

  function sendReply(e) {
    e.preventDefault();
    if (!selected || !reply.trim()) return;
    startTransition(async () => {
      try {
        const data = await api.post(`/server/attorney_api/matter/${selected.ROWID}/messages`, {
          body: reply.trim(),
        });
        setMessages((prev) => [...prev, data.message]);
        setReply('');
      } catch {
        const message = {
          ROWID: `msg_local_${Date.now()}`,
          Matter_ID: selected.ROWID,
          Sender_Role: 'ATTORNEY',
          Sender_Name: user?.displayName || user?.email || 'Attorney',
          Body: reply.trim(),
          Created_At: new Date().toISOString(),
        };
        setMessages(appendLocalMessage(selected.ROWID, message));
        setReply('');
      }
    });
  }

  function deleteMessage(message) {
    if (!selected?.ROWID || !message?.ROWID || deletingMessageId) return;
    setDeletingMessageId(message.ROWID);
    startTransition(() => {
      const removed = deleteLocalMessage(selected.ROWID, message.ROWID);
      setDeletingMessageId('');
      if (!removed) return;
      setMessages((prev) =>
        (prev || []).filter((item) => String(item?.ROWID) !== String(message.ROWID))
      );
      flashSave('Message deleted.');
    });
  }

  function fulfill() {
    if (!selected) return;
    startTransition(async () => {
      try {
        const data = await api.post('/server/attorney_api/fulfillDemand', {
          matterId: selected.ROWID,
          trackingNumber: tracking,
          demandContent: draft,
          dueDate: dueDate || null,
        });
        setQueue((prev) => prev.filter((m) => m.ROWID !== selected.ROWID));
        setSelectedId(null);
        flashSave(`Dispatched. Locker: ${data.vault?.url || 'saved'}`);
      } catch {
        applyDueDateLocally(selected.ROWID, dueDate);
        setQueue((prev) => prev.filter((m) => m.ROWID !== selected.ROWID));
        setSelectedId(null);
        flashSave(`Certified mail recorded locally: ${tracking}`);
      }
    });
  }

  return (
    <div className="app-shell attorney-shell">
      <header className="app-topbar">
        <div className="topbar-brand">
          <BrandMark size={36} />
          <div>
            <span>Attorney Workbench</span>
          </div>
        </div>
        <div className="topbar-actions">
          <span className="user-chip">{user?.email}</span>
          <Link className="btn-ghost" to="/settings">
            Settings
          </Link>
          <button type="button" className="btn-ghost" onClick={logout}>
            Sign out
          </button>
        </div>
      </header>

      <main className="attorney-layout">
        <aside className="attorney-queue">
          <h1>Jurisdiction queue</h1>
          <p>Cases matched to your licensed states.</p>
          <ul>
            {queue.map((m) => (
              <li key={m.ROWID}>
                <button
                  type="button"
                  className={m.ROWID === selectedId ? 'queue-item active' : 'queue-item'}
                  onClick={() => selectMatter(m)}
                >
                  <strong>{m.Debtor_Name}</strong>
                  <span>
                    {m.Debtor_State} · {formatMatterMoney(m.Amount_Owed, m)}
                  </span>
                </button>
              </li>
            ))}
            {!queue.length ? <li className="empty-state compact">Queue clear.</li> : null}
          </ul>
        </aside>

        <section className="attorney-split">
          {!selected ? (
            <div className="empty-state full">Select a matter to open the workbench.</div>
          ) : (
            <>
              <div className="workbench-stepper-bar">
                <StatusStepper status={selected.Status} />
              </div>
              <div className="evidence-pane">
                <h2>Client files</h2>
                <p>Evidence package and matter facts for {selected.Debtor_Name}.</p>
                <dl className="fact-list">
                  <div>
                    <dt>Debtor</dt>
                    <dd>{selected.Debtor_Name}</dd>
                  </div>
                  <div>
                    <dt>Country</dt>
                    <dd>
                      {{
                        CA: 'Canada',
                        GB: 'UK',
                        UK: 'UK',
                        AU: 'Australia',
                        NZ: 'New Zealand',
                        IE: 'Ireland',
                      }[String(selected.Debtor_Country || '').toUpperCase()] || 'USA'}
                    </dd>
                  </div>
                  <div>
                    <dt>State / province</dt>
                    <dd>{selected.Debtor_State}</dd>
                  </div>
                  <div>
                    <dt>Amount</dt>
                    <dd>{formatMatterMoney(selected.Amount_Owed, selected)}</dd>
                  </div>
                  <div>
                    <dt>Origin</dt>
                    <dd>{selected.Origin}</dd>
                  </div>
                  <div>
                    <dt>Due date</dt>
                    <dd>{dueDate || selected.Due_Date || 'Not set'}</dd>
                  </div>
                </dl>
                <a
                  className="evidence-link"
                  href={selected.Evidence_Vault_URL || '#'}
                  onClick={(e) => e.preventDefault()}
                >
                  {selected.Evidence_Vault_URL || 'No evidence package uploaded'}
                </a>
                <div className="clause-block">
                  <h3>Jurisdiction clauses</h3>
                  <pre>{selected.Jurisdiction_Clauses || 'No clauses preloaded.'}</pre>
                </div>

                <div className="attorney-comms">
                  <h3>Client communication</h3>
                  <div className="message-thread compact-thread" ref={threadRef}>
                    {messages.length ? (
                      messages.map((msg) => (
                        <MessageBubble
                          key={msg.ROWID}
                          message={msg}
                          showTime={false}
                          canDelete
                          deleting={deletingMessageId === msg.ROWID}
                          deleteDisabled={Boolean(deletingMessageId) || pending}
                          onDelete={deleteMessage}
                        />
                      ))
                    ) : (
                      <p className="empty-inline">No client messages yet.</p>
                    )}
                  </div>
                  <MessageComposer
                    label="Reply to client"
                    placeholder="Update the client…"
                    draft={reply}
                    onDraftChange={setReply}
                    onSubmit={sendReply}
                    pending={pending}
                    rows={2}
                    submitLabel="Send"
                    showAttachments={false}
                  />
                </div>
              </div>

              <div className="workbench-pane">
                <h2>Demand letter workbench</h2>
                <p>Markdown draft preloaded with local compliance language.</p>
                <textarea
                  className="markdown-editor"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  spellCheck={false}
                />

                <div className="tracking-panel">
                  <label className="field">
                    <span className="field-label-with-tip">
                      Payment due date
                    </span>
                    <input
                      type="date"
                      value={dueDate}
                      onChange={(e) => {
                        setDueDate(e.target.value);
                        clearSaveToast();
                      }}
                    />
                    <span className="field-hint">
                      Clients only see this after you save it. Past due with no payment marks the
                      demand Unpaid.
                    </span>
                  </label>
                  <div className="tracking-panel-actions">
                    <button
                      type="button"
                      className="btn-ghost"
                      disabled={pending}
                      onClick={saveDueDate}
                    >
                      {pending ? 'Saving…' : 'Save due date'}
                    </button>
                  </div>
                  <label className="field">
                    <span>Certified mail tracking number</span>
                    <input
                      value={tracking}
                      onChange={(e) => setTracking(e.target.value)}
                      placeholder="9400 1000 0000 0000 0000 00"
                      required
                    />
                  </label>
                  <div className="tracking-panel-actions">
                    {hasTrackingNumber(tracking) ? (
                      <a
                        className="btn-ghost"
                        href={mailTrackingUrl(tracking, {
                          country: selected?.Debtor_Country,
                          postalCode: selected?.Debtor_Postal_Code,
                          state: selected?.Debtor_State,
                        })}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {mailCarrierLabel(tracking, {
                          country: selected?.Debtor_Country,
                          postalCode: selected?.Debtor_Postal_Code,
                          state: selected?.Debtor_State,
                        })}
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="btn-ghost"
                        disabled
                        title="Enter a tracking number to open USPS or Canada Post tracking"
                      >
                        Track package
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={pending || !tracking.trim()}
                      onClick={fulfill}
                    >
                      {pending ? 'Finalizing…' : 'Fulfill & dispatch'}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </section>
      </main>

    </div>
  );
}
